import { useState } from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { fmt, fmtCur, fmtWt, MarginBadge, PlatingSupplierBubble, isNegativePart } from './helpers';
import { PartNoteButton } from './PartNote';

export function CostBreakdownModal({ part, onClose, platingInfo, showPlatingSupplier = false }) {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const canExport = user?.role === 'admin' || (user?.capabilities || []).includes('export_single_customer');

  if (!part) return null;
  const scrapWt = ((part.gross_weight || 0) - (part.net_weight || 0));
  const toolPerPiece = part.tool_maintenance_cost || (part.tooling_life > 0 ? (part.tooling_cost || 0) / part.tooling_life : 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/parts/${part.part_id}/export-costing`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `costing_${part.part_number || 'part'}.xlsx`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Costing exported to Excel');
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'You cannot export costing data.' : 'Failed to export costing');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-6">
            <DialogTitle className="font-heading text-slate-900">
              Costing Breakdown — <span className="font-mono text-orange-500">{part.part_number}</span>
            </DialogTitle>
            <div className="shrink-0 flex items-center gap-2">
              <PartNoteButton part={part} size={16} attention={isNegativePart(part) || part.rm_variance?.status === 'behind'} />
              {canExport && (
                <button onClick={handleExport} disabled={exporting}
                  data-testid="export-costing-btn"
                  title="Download this part's costing as an Excel file (with live formulas)"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-sm border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 transition-colors">
                  <FileSpreadsheet size={14} />
                  {exporting ? 'Exporting…' : 'Export to Excel'}
                </button>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-1 mt-2" data-testid="cost-breakdown-body">

          {/* 1. Gross RMC */}
          <div className="flex items-center justify-between py-2 border-b border-slate-100" data-testid="gross-rmc-row">
            <div>
              <p className="text-sm font-semibold text-slate-700">Gross RMC</p>
              <p className="text-xs text-slate-400">{fmtWt(part.gross_weight)} kg × ₹{fmt(part.rm_price)}/kg</p>
            </div>
            <p className="font-mono text-sm font-bold text-slate-900">{fmtCur(part.gross_rm_cost)}</p>
          </div>

          {/* 2. Less: Scrap Recovery */}
          <div className="flex items-center justify-between py-2 border-b border-slate-100" data-testid="scrap-recovery-row">
            <div>
              <p className="text-sm font-semibold text-emerald-700">Less: Scrap Recovery</p>
              <p className="text-xs text-slate-400">{fmtWt(scrapWt)} kg × ₹{fmt(part.scrap_price_per_kg)}/kg{part.scrap_recovery_pct != null && part.scrap_recovery_pct < 100 ? ` × ${fmt(part.scrap_recovery_pct)}%` : ''}</p>
            </div>
            <p className="font-mono text-sm font-bold text-emerald-600">−{fmtCur(part.scrap_recovery)}</p>
          </div>

          {/* 3. Net RMC (highlighted) */}
          <div className="flex items-center justify-between py-3 bg-slate-50 px-3 rounded-sm" data-testid="net-rmc-row">
            <div>
              <p className="font-bold text-slate-900">Net RMC</p>
              <p className="text-[10px] text-slate-400">Gross RMC − Scrap Recovery</p>
            </div>
            <p className="font-mono font-black text-lg text-slate-900">{fmtCur(part.rm_cost_net)}</p>
          </div>

          {/* 4. Process Cost */}
          <div className="py-2 border-b border-slate-100" data-testid="process-cost-rows">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Process Cost</p>
              <p className="font-mono text-sm font-bold text-slate-900">{fmtCur(part.process_cost)}</p>
            </div>
            {part.processes?.length > 0 && part.processes.some(p => p.cost > 0) && (
              <div className="mt-1">
                {part.processes.filter(p => p.cost > 0).map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-0.5 pl-3">
                    <p className="text-xs text-slate-500">{p.name}</p>
                    <p className="font-mono text-xs font-semibold text-slate-600">{fmtCur(p.cost)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 5. Plating Cost */}
          <div className="flex items-center justify-between py-2 border-b border-slate-100" data-testid="plating-cost-row">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-purple-700">Plating Cost</p>
              {showPlatingSupplier && (part.plating_cost || 0) > 0 && (
                <PlatingSupplierBubble info={platingInfo} customerRate={part.plating_cost} />
              )}
            </div>
            <p className="font-mono text-sm font-bold text-purple-600">{fmtCur(part.plating_cost)}</p>
          </div>

          {/* 6. BOP Cost */}
          <div className="py-2 border-b border-slate-100" data-testid="bop-cost-rows">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-indigo-700">BOP Cost</p>
              <p className="font-mono text-sm font-bold text-indigo-700">{fmtCur(part.total_bop_cost)}</p>
            </div>
            {part.bop_items?.length > 0 && part.bop_items.some(b => b.cost > 0) && (
              <div className="mt-1">
                {part.bop_items.filter(b => b.cost > 0).map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-0.5 pl-3">
                    <p className="text-xs text-slate-500">{b.name}</p>
                    <p className="font-mono text-xs font-semibold text-indigo-600">{fmtCur(b.cost)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 7. Tool Maintenance Cost */}
          <div className="flex items-center justify-between py-2 border-b border-slate-100" data-testid="tool-maintenance-row">
            <div>
              <p className="text-sm font-semibold text-slate-700">Tool Maintenance</p>
              {part.tooling_cost > 0 && <p className="text-xs text-slate-400">₹{fmt(part.tooling_cost)} / {fmt(part.tooling_life)} pcs</p>}
            </div>
            <p className="font-mono text-sm font-bold text-slate-900">{fmtCur(toolPerPiece)}</p>
          </div>

          {/* 8. Total Manufacturing Cost (highlighted) */}
          <div className="flex items-center justify-between py-3 bg-slate-100 px-3 rounded-sm" data-testid="total-mfg-cost-row">
            <div>
              <p className="font-bold text-slate-900">Total Mfg Cost</p>
              <p className="text-[10px] text-slate-400">Net RMC + Process + Plating + BOP + Tool Maint</p>
            </div>
            <p className="font-mono font-black text-lg text-slate-900">{fmtCur(part.total_manufacturing_cost)}</p>
          </div>

          {/* 9. Fixed Costs Section */}
          {((part.overhead_fixed || 0) > 0 || (part.packing_fixed || 0) > 0 || (part.tool_maintenance_fixed || 0) > 0) && (
            <div className="py-2 border-b border-slate-100" data-testid="fixed-costs-section">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Fixed Costs</p>
              <div className="flex items-center justify-between py-0.5 pl-3">
                <p className="text-xs text-slate-600">Overhead Fixed</p>
                <p className="font-mono text-xs font-semibold text-slate-700">{fmtCur(part.overhead_fixed)}</p>
              </div>
              <div className="flex items-center justify-between py-0.5 pl-3">
                <p className="text-xs text-slate-600">Packing Fixed</p>
                <p className="font-mono text-xs font-semibold text-slate-700">{fmtCur(part.packing_fixed)}</p>
              </div>
              <div className="flex items-center justify-between py-0.5 pl-3">
                <p className="text-xs text-slate-600">Tool Maintenance Fixed</p>
                <p className="font-mono text-xs font-semibold text-slate-700">{fmtCur(part.tool_maintenance_fixed)}</p>
              </div>
            </div>
          )}

          {/* 10. Selling Price */}
          <div className="flex items-center justify-between py-2 px-3 mt-2 border-t border-slate-200" data-testid="selling-price-row">
            <p className="text-sm font-semibold text-slate-700">Selling Price</p>
            <p className="font-mono text-sm font-bold text-slate-900">{fmtCur(part.selling_price)}</p>
          </div>

          {/* 11. Reconciliation — Selling (PO) minus the summed components. Target: ₹0. */}
          {(() => {
            const gap = Number(part.unallocated_balance ?? part.contribution) || 0;
            const reconciled = Math.abs(gap) < 0.01;
            const over = gap < -0.01;
            const rowBg = reconciled ? 'bg-emerald-50' : over ? 'bg-red-50' : 'bg-amber-50';
            const valCls = reconciled ? 'text-emerald-700' : over ? 'text-red-700' : 'text-amber-700';
            const title = reconciled ? 'Reconciled' : over ? 'Over PO price' : 'Unallocated balance';
            const note = reconciled
              ? 'Components add up exactly to the PO price ✓'
              : over
                ? 'Components exceed the PO price'
                : 'Allocate into components to reach ₹0';
            return (
              <div className={`flex items-center justify-between py-3 px-3 rounded-sm ${rowBg}`} data-testid="reconciliation-row">
                <div>
                  <p className="font-bold text-slate-900">{title}</p>
                  <p className="text-[10px] text-slate-500">Selling (PO) − Total Mfg Cost. {note}</p>
                </div>
                <div className="text-right">
                  <p className={`font-mono font-black text-lg ${valCls}`}>{fmtCur(gap)}</p>
                  {reconciled
                    ? <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">Balanced</span>
                    : <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium font-mono ${over ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>{over ? 'Over by' : 'To allocate'}: {fmtCur(Math.abs(gap))}</span>}
                </div>
              </div>
            );
          })()}

          {/* 12. Margin = Add-on + Unallocated balance */}
          <div className="flex items-center justify-between py-2 px-3" data-testid="margin-row">
            <div>
              <p className="text-sm font-semibold text-slate-700">Margin</p>
              <p className="text-[10px] text-slate-400">Add-on + Unallocated balance</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-sm font-bold text-slate-900">{fmtCur(part.margin_value ?? part.contribution)}</p>
              <MarginBadge pct={part.margin_pct} threshold={15} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
